/** Shared dependencies and persisted record types of the backend services. */
import type { Clock } from '../../../shared/clock';
import type { JsonValue } from '../../../shared/json';
import type { RandomSource } from '../../../shared/random';
import type { ProtocolError } from '../../../shared/protocol/errors';
import type { BanInfo, CommandVolatile, DeviceInfo, PlayerId, PlayerProfile, TeamSnapshot } from '../../../shared/protocol/model';
import type { BackendConfig } from '../config';
import type { EventHub } from '../hub';
import type { Logger } from '../logger';
import type { KeyedMutex } from '../mutex';
import type { StorageAdapter } from '../storage';
import type { EconAnchor } from './economy';

export interface ServiceDeps {
  storage: StorageAdapter;
  clock: Clock;
  rng: RandomSource;
  logger: Logger;
  hub: EventHub;
  config: BackendConfig;
  mutex: KeyedMutex;
  /** Random source of the rule ops (roulette, steals, crafting, battle seeds); default: `rng`. */
  ruleRng: RandomSource;
  /**
   * TEST / DEVELOPMENT SWITCH (BackendCoreOptions.fixedBattleSeed, the server's BATTLE_SEED): the seed of every battle
   * the backend starts (battle.start, solo and online sessions) instead of a random one; null = random seeds (always in
   * production).
   */
  fixedBattleSeed: number | null;
}

/** Storage collections. */
export const C = {
  accounts: 'accounts',
  usernames: 'usernames',
  devices: 'devices',
  /** Session tokens of the protocol 1 backend (sha256 id); auth.resume converts a live one into a device session. */
  tokens: 'tokens',
  /** Device sessions of an account, one document per account (id = playerId): AccountSessionsRecord. */
  authSessions: 'auth_sessions',
  /** Access and refresh tokens (id = keyed hash of the token): AuthTokenRecord. */
  authTokens: 'auth_tokens',
  profiles: 'profiles',
  grants: 'grants',
  zones: 'zones',
  /** Player-made clans, one document per clan (id = clanId): services/clan-directory.ts ClanRecord. */
  clans: 'clans',
  /** The uniqueness index of clan names (id = clanNameKey of the name, src/shared/clan-rules.ts): ClanNameRecord. */
  clanNames: 'clan_names',
  /**
   * Clan membership, one document per player who ever was in a clan or created one (id = playerId): ClanPlayerRecord,
   * the ONLY record of who is in which clan with which role, and of the player's clan cooldowns.
   */
  clanPlayers: 'clan_players',
  /** Pending requests to join a clan (id = `<clanId>~<playerId>`): ClanRequestRecord. */
  clanRequests: 'clan_requests',
  /** The history of a clan, one document per clan (id = clanId): ClanHistoryRecord. */
  clanHistory: 'clan_history',
  /**
   * Chat messages, one document per message (id = messageId): services/chat.ts StoredChatMessage; each channel keeps its
   * last config.chat.history* messages (older ones are deleted with the write of a new one). Deleted with the account.
   */
  chatMessages: 'chat_messages',
  /** A player's read positions of the chat channels (id = playerId): services/chat.ts ChatReadRecord. */
  chatReads: 'chat_reads',
  /** Operators' mutes of the chat (id = playerId): services/chat.ts ChatMuteRecord. */
  chatMutes: 'chat_mutes',
  /** The annihilator auction: one document (id AUCTION_DOC_ID, services/auction.ts AuctionRecord). */
  auction: 'auction',
  replays: 'replays',
  battles: 'battles',
  /** Rooms that are not closed, one document per room (services/rooms.ts LiveRoomDoc), restored at startup. */
  liveRooms: 'live_rooms',
  /** Active battle sessions (services/sessions.ts LiveSessionDoc), restored at startup; an ended one moves to `battles`. */
  liveSessions: 'live_sessions',
  news: 'news',
  /** Default display names handed out to guests (id = lower-case name), kept so that no name is issued twice. */
  defaultNames: 'default_names',
  /**
   * Display names chosen by accounts without a nickname (guests, username accounts: auth.guest / auth.register
   * displayName, profile.setDisplayName), id = the name's nickname key (src/shared/auth-rules.ts): DisplayNameRecord.
   * They share the one namespace of nicknames and logins, so no account can show the name another one has.
   */
  displayNames: 'display_names',
  /**
   * Replies of mutations for retries under the same request id after a restart (backend.ts; id = keyed hash of the
   * idempotency key, the reply sealed under another keyed hash of it: no token or request id in the clear), pruned
   * after config.idempotency.ttlMs.
   */
  idempotency: 'idempotency',
  /**
   * Account deletions whose batch is stored but whose later steps are not all done yet (id = playerId):
   * services/account-deletion.ts AccountDeletionRecord, written in the deletion's batch and deleted once those steps
   * are done; a record left by a failure or a crash is finished by the next deletion call, the sweep or the next start.
   */
  accountDeletions: 'account_deletions',
  /**
   * Store purchases, one document per purchase (id = purchaseId: a hash of the provider and the purchase token):
   * services/store.ts PurchaseRecord. Kept after an account deletion (financial record: refunds, chargebacks).
   */
  purchases: 'purchases',
  /** The index of order ids (id = the store's order id) -> purchaseId. */
  purchaseOrders: 'purchase_orders',
  /** Per-player purchase counts of the limited products (id = playerId): services/store.ts StorePlayerRecord. */
  storePlayers: 'store_players',
  /** The obfuscated account ids of the store (id = the tag) -> playerId: services/store.ts StoreAccountRecord. */
  storeAccounts: 'store_accounts',
  /** Rewarded ad tickets (id = ticketId): services/ads.ts AdTicketRecord. */
  adTickets: 'ad_tickets',
  /** Per-player ad state (id = playerId): today's counts, the open tickets, the rewarded contexts. */
  adPlayers: 'ad_players',
  /** The ad provider's transaction ids already seen (id = hash of provider + transaction id) -> ticketId. */
  adTransactions: 'ad_transactions',
  /** The audit log of purchases and ad rewards (id sorts by time): services/monetization-audit.ts. */
  monetizationAudit: 'monetization_audit',
  /** Google Play Games links (id = the Play Games player id) -> playerId: services/play-games.ts PlayGamesLinkRecord. */
  playGamesLinks: 'play_games_links',
  /** The reverse of playGamesLinks (id = playerId) -> the Play Games player id. */
  playGamesPlayers: 'play_games_players',
  /**
   * Confirmed sign-in addresses (id = services/email.ts emailIndexId: SHA-256 of the address's emailKey) -> the account:
   * services/email.ts EmailIndexRecord. Only confirmed addresses are here: a pending one blocks nobody.
   */
  emails: 'emails',
  /** The e-mail state of an account (id = playerId): its address, the pending one, its open links. */
  emailAccounts: 'email_accounts',
  /** Open links of e-mails (id = keyed hash of the link's token): services/email.ts EmailTokenRecord. */
  emailTokens: 'email_tokens',
  /** The audit log of account security events (id sorts by time): services/account-audit.ts. */
  accountAudit: 'account_audit',
  /** The latest Play Integrity verdict of a player (id = playerId): services/integrity.ts IntegrityRecord. */
  integrity: 'integrity',
  /** Push tokens (id = keyed hash of the token) -> the account: services/push.ts PushTokenRecord. */
  pushTokens: 'push_tokens',
  /**
   * The resource ledger (id sorts by time): every gold / diamond change an operator made to a player's canonical state
   * (grants, take-aways, state edits, progress resets), services/resource-ledger.ts ResourceLedgerEntry, written in the
   * same storage batch as the state it changed.
   */
  resourceLedger: 'resource_ledger',
  /**
   * The anti-cheat's marks (id = `<playerId>~<rule>`: one mark per player and rule, repeats aggregate into it):
   * services/anticheat.ts AnticheatMark. Deleted with the account.
   */
  anticheatMarks: 'anticheat_marks',
  /** The anti-cheat's rolling per-player state (id = playerId): services/anticheat.ts PlayerWatch. */
  anticheatPlayers: 'anticheat_players',
} as const;

export interface AccountRecord {
  playerId: PlayerId;
  displayName: string;
  createdAt: number;
  updatedAt: number;
  lastSeenAt: number;
  /**
   * Lower-case login, null for guest-only accounts: the username of auth.register, or the nickname key
   * (src/shared/auth-rules.ts nicknameKey) of auth.signUp. Both kinds share the index C.usernames (one namespace).
   */
  username: string | null;
  /** The nickname as typed at auth.signUp (NFC, trimmed, case kept); absent for guest and username accounts. */
  nickname?: string | null;
  passwordHash: string | null;
  badges: string[];
  ban: BanInfo | null;
  // Clan membership is not here: C.clanPlayers holds it (services/clan-directory.ts; the storage migration
  // drop-default-clans removed the former `clanId` of the fixed clans of the original).
  /** Server-recorded successful zone captures (clan rating). */
  captures: number;
  /** Next moment tribute may be claimed (0 = any time). */
  nextTributeAt: number;
}

/** A display name held by an account without a nickname (C.displayNames, id = the name's nickname key). */
export interface DisplayNameRecord {
  name: string;
  playerId: PlayerId;
  at: number;
}

/** Protocol 1 session token (C.tokens, id = SHA-256 of the token); read only to convert it (auth.resume). */
export interface TokenRecord {
  tokenHash: string;
  playerId: PlayerId;
  createdAt: number;
  expiresAt: number;
  lastUsedAt: number;
}

/** Guest device token (C.devices, id = keyed hash of the token; SHA-256 for records of an earlier backend). */
export interface DeviceRecord {
  deviceHash: string;
  playerId: PlayerId;
  createdAt: number;
  lastUsedAt: number;
}

/** One device session (AccountSessionsRecord.sessions). Tokens are referenced by their keyed hashes only. */
export interface AuthSessionRecord {
  sessionId: string;
  device: DeviceInfo;
  createdAt: number;
  lastSeenAt: number;
  /** How the session was opened. */
  method: 'guest' | 'register' | 'login' | 'resume' | 'signup' | 'signin' | 'play_games' | 'email';
  /** Keyed hash of the guest device token that opened the session, if any. */
  deviceHash: string | null;
  /** Rotation counter: 0 at sign-in, +1 per refresh (and per auth.link). */
  generation: number;
  /** Keyed hash of the current refresh token; the session ends when it expires. */
  refreshHash: string;
  refreshExpiresAt: number;
  /** Keyed hash of the refresh token the last rotation replaced (reuse within the grace window is not theft). */
  previousRefreshHash: string | null;
  /**
   * Keyed hashes of the replaced refresh tokens whose records are kept for reuse detection, oldest first (bounded:
   * older ones are deleted, so a very old stolen token is merely unknown).
   */
  rotatedHashes?: string[];
  /** Time of the last rotation (0 = never rotated). */
  rotatedAt: number;
  /**
   * The auth.refresh request that made the last rotation: the keyed hash of its request id and of the access token it
   * issued. A retry of that request (the same id with the previous refresh token) within
   * config.auth.refreshRetryWindowMs is answered with a valid pair instead of counting as a reuse, even after a server
   * restart. Absent / null: the last rotation was no refresh (auth.link) or came from an earlier backend.
   */
  refreshRequest?: { idHash: string; accessHash: string } | null;
}

/** The device sessions of one account (C.authSessions, id = playerId), bounded by auth.maxSessionsPerAccount. */
export interface AccountSessionsRecord {
  playerId: PlayerId;
  sessions: AuthSessionRecord[];
  /**
   * Sessions a newer sign-in of the account ended (config.auth.singleSession), newest last, bounded
   * (REPLACED_SESSIONS_KEPT) and kept for at most config.auth.refreshTtlMs: their tokens are then refused with
   * 'session_replaced' instead of 'revoked', so the replaced client tells its player why. Absent in older documents.
   */
  replaced?: ReplacedSessionRecord[];
}

/** A session ended because the account signed in (or resumed a session) somewhere else (AccountSessionsRecord.replaced). */
export interface ReplacedSessionRecord {
  sessionId: string;
  /** When it was replaced. */
  at: number;
  /** The session that replaced it and that session's platform (DeviceInfo.platform). */
  by: string;
  byPlatform: string;
}

/**
 * An issued access or refresh token (C.authTokens, id = keyed hash of the token). Rotated refresh tokens stay until
 * they expire so that a reuse is recognized; tokens of an ended session answer 'revoked' until the sweep drops them.
 */
export interface AuthTokenRecord {
  /** The document id (keyed hash of the token). */
  hash: string;
  kind: 'access' | 'refresh';
  playerId: PlayerId;
  sessionId: string;
  /** Refresh tokens: the rotation generation that issued it. */
  generation: number;
  createdAt: number;
  expiresAt: number;
}

export interface UsernameRecord {
  username: string;
  playerId: PlayerId;
}

/** One player.command outcome kept in the profile document for retries of the same opId. */
export interface CommandJournalEntry {
  opId: string;
  /** SHA-256 of the canonical JSON of { op, args }: a reused opId with another command is refused. */
  hash: string;
  op: string;
  /** Server time of the command (epoch ms). */
  at: number;
  /** Success: the response without the game state (the state is the profile's, see CommandService). */
  ok: boolean;
  revision?: number;
  result?: JsonValue;
  volatile?: CommandVolatile;
  /** Failure (a rule precondition): the error the command got. Failures do not change the state or the revision. */
  error?: ProtocolError;
}

/** The player's calendar time zone (minutes east of UTC) and when it was last set. */
export interface StoredTimeZone {
  offsetMin: number;
  /** Epoch ms of the last change (a new offset is accepted at most once per 24 hours). */
  changedAt: number;
}

/**
 * The stored profile document. `profile.gameState` is the canonical game state; the other fields are server-owned and
 * never sent to clients as such. Every write of the document carries all of them (one atomic document write).
 */
export interface ProfileRecord {
  profile: PlayerProfile;
  team: TeamSnapshot | null;
  /**
   * Server state next to the game state (design D1 `serverState`): what the original kept only in the client's
   * memory (a pending steal apology, today's present, the in-flight battle, ...). Keys belong to the ops that use them.
   */
  serverState?: { [key: string]: JsonValue };
  /** The last player.command outcomes, oldest first (config.commands.journalSize). */
  journal?: CommandJournalEntry[];
  tz?: StoredTimeZone;
  /**
   * The game state is the server-created initial state (profile.create, then only session entries): no command, save
   * or import changed it yet. Such a profile may still be replaced by an import (config.allowLegacyImport).
   */
  fresh?: boolean;
  /**
   * The revision of an initial state that auth.signUp created and no session entry has run on yet: the first
   * profile.load at that revision enters it like a state it created itself (session.enter { created: true }; with an
   * allowed `dev` it builds the developer profile instead). Any other revision: nothing pending.
   */
  entryPending?: number;
  /**
   * The gold and diamonds of `profile.gameState` as the last instrumented write left them (./economy.ts): a stored
   * balance that differs was changed by no known source. Absent in documents older than the anti-cheat (the next write
   * sets it) and while the profile has no state the rules can read.
   */
  econ?: EconAnchor;
}
